import React, { useState, useRef, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/apiConfig';
import { v4 as uuidv4 } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faCamera, faSpinner, faImages, faTimes } from '@fortawesome/free-solid-svg-icons';

const EnhancedImageUpload = ({ 
    sessionId, 
    onImageUploaded, 
    maxImages, 
    currentCount,
    tileWidth = 300,
    tileHeight = 300,
    disabled = false,
    buttonClassName = "",
    buttonText = "Upload Images",
    buttonIcon = null
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);
    const [showOptions, setShowOptions] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const dropdownRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    
    // Detect if the device is mobile
    useEffect(() => {
        const checkMobile = () => {
            const userAgent = navigator.userAgent || navigator.vendor || window.opera;
            const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
            setIsMobile(isMobileDevice);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);
    
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowOptions(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Determine if we can still add more images
    const canAddMoreImages = currentCount < maxImages;
    
    // Handle file selection (from file input)
    const handleFileSelect = async (event) => {
        try {
            const files = Array.from(event.target.files);
            if (!files.length) return;
            
            // Close the options modal immediately when files are selected
            setShowOptions(false);
            
            // Check if adding these files would exceed the limit
            if (currentCount + files.length > maxImages) {
                setError(`You can only upload a total of ${maxImages} images. Please select ${maxImages - currentCount} or fewer.`);
                setTimeout(() => setError(null), 5000);
                return;
            }
            
            setIsUploading(true);
            setError(null);
            
            // Process each file
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                setUploadProgress(Math.round(((i) / files.length) * 100));
                
                // Create placeholder while file uploads
                const placeholderKey = `placeholder_${uuidv4()}`;
                const placeholderUrl = URL.createObjectURL(file);
                
                // Add placeholder image to UI immediately
                onImageUploaded({
                    url: placeholderUrl,
                    placeholderUrl: placeholderKey,
                    originalName: file.name,
                    isUploading: true,
                    isLocalPreview: true,
                    // Pass tile dimensions to placeholder for proper aspect ratio
                    tile_width: tileWidth,
                    tile_height: tileHeight
                });
                
                // Create FormData for the API request
                const formData = new FormData();
                formData.append('session_id', sessionId);
                formData.append('image', file);
                
                // Add tile dimensions if specified
                if (tileWidth) formData.append('tile_width', tileWidth.toString());
                if (tileHeight) formData.append('tile_height', tileHeight.toString());
                
                try {
                    // Send the image to the server for processing
                    const response = await fetch(API_ENDPOINTS.PROCESS_TSHIRT, {
                        method: 'POST',
                        body: formData,
                    });
                    
                    if (!response.ok) {
                        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
                    }
                    
                    const data = await response.json();
                    
                    // Replace placeholder with actual processed image
                    onImageUploaded({
                        url: data.tile_url,
                        tshirtUrl: data.original_url || data.tshirt_url,
                        originalUrl: data.original_url,
                        placeholderKey: placeholderKey,
                        originalName: file.name,
                        dominantColor: data.dominant_color,
                        processed: true,
                        // Add tile dimensions for proper aspect ratio display
                        tile_width: tileWidth,
                        tile_height: tileHeight
                    });
                    
                } catch (uploadError) {
                    console.error("Error uploading image:", uploadError);
                    
                    // Remove placeholder on error
                    onImageUploaded({
                        action: 'remove',
                        placeholderUrl: placeholderKey
                    });
                    
                    setError(`Error uploading "${file.name}": ${uploadError.message}`);
                    setTimeout(() => setError(null), 5000);
                }
                
                // Clean up object URL
                URL.revokeObjectURL(placeholderUrl);
            }
            
            // Reset input value to allow re-selecting the same file
            if (fileInputRef.current) fileInputRef.current.value = '';
            
        } catch (error) {
            console.error("Error in file selection:", error);
            setError(`An unexpected error occurred: ${error.message}`);
            setTimeout(() => setError(null), 5000);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
            setShowOptions(false);
        }
    };

    // Handle capturing an image from camera
    const handleCameraCapture = async (event) => {
        try {
            const files = Array.from(event.target.files);
            if (!files.length) return;
            
            // Close the options modal immediately when camera capture is done
            setShowOptions(false);
            
            if (currentCount + files.length > maxImages) {
                setError(`You can only upload a total of ${maxImages} images.`);
                setTimeout(() => setError(null), 5000);
                return;
            }
            
            setIsUploading(true);
            setError(null);
            
            // Process each captured photo (usually just one)
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                setUploadProgress(Math.round(((i) / files.length) * 100));
                
                // Create a placeholder for the captured photo
                const placeholderKey = `camera_${uuidv4()}`;
                const placeholderUrl = URL.createObjectURL(file);
                
                // Add placeholder to UI immediately
                onImageUploaded({
                    url: placeholderUrl,
                    placeholderUrl: placeholderKey,
                    originalName: `camera_capture_${new Date().toISOString()}.jpg`,
                    isUploading: true,
                    isLocalPreview: true,
                    // Add tile dimensions for proper aspect ratio display
                    tile_width: tileWidth,
                    tile_height: tileHeight
                });
                
                // Create FormData for the API request
                const formData = new FormData();
                formData.append('session_id', sessionId);
                formData.append('image', file);
                
                // Add tile dimensions if specified
                if (tileWidth) formData.append('tile_width', tileWidth.toString());
                if (tileHeight) formData.append('tile_height', tileHeight.toString());
                
                try {
                    // Send the captured image to the server for processing
                    const response = await fetch(API_ENDPOINTS.PROCESS_TSHIRT, {
                        method: 'POST',
                        body: formData,
                    });
                    
                    if (!response.ok) {
                        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
                    }
                    
                    const data = await response.json();
                    
                    // Replace placeholder with actual processed image
                    onImageUploaded({
                        url: data.tile_url,
                        tshirtUrl: data.original_url || data.tshirt_url,
                        originalUrl: data.original_url,
                        placeholderKey: placeholderKey,
                        originalName: `camera_capture_${new Date().toISOString()}.jpg`,
                        dominantColor: data.dominant_color,
                        processed: true,
                        // Add tile dimensions for proper aspect ratio display
                        tile_width: tileWidth,
                        tile_height: tileHeight
                    });
                    
                } catch (uploadError) {
                    console.error("Error uploading camera capture:", uploadError);
                    
                    // Remove placeholder on error
                    onImageUploaded({
                        action: 'remove',
                        placeholderUrl: placeholderKey
                    });
                    
                    setError(`Error uploading camera capture: ${uploadError.message}`);
                    setTimeout(() => setError(null), 5000);
                }
                
                // Clean up object URL
                URL.revokeObjectURL(placeholderUrl);
            }
            
            // Reset input value to allow capturing more photos
            if (cameraInputRef.current) cameraInputRef.current.value = '';
            
        } catch (error) {
            console.error("Error in camera capture:", error);
            setError(`An unexpected error occurred: ${error.message}`);
            setTimeout(() => setError(null), 5000);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
            setShowOptions(false);
        }
    };

    // Handle clicking the upload button
    const handleUploadClick = () => {
        if (disabled) return;
        
        if (isMobile) {
            // On mobile, show options dropdown
            setShowOptions(true);
        } else {
            // On desktop, directly open file selector
            fileInputRef.current.click();
        }
    };
    
    // Handle take photo option
    const handleTakePhotoClick = () => {
        if (cameraInputRef.current) {
            cameraInputRef.current.click();
        }
        // Modal will be closed after photo is captured
    };
    
    // Handle choose file option
    const handleChooseFileClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
        // Modal will be closed after files are selected
    };

    // Handle file selection button click
    const handleFileSelectClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
        // Don't close modal yet - wait for actual file selection
    };

    // Handle camera button click
    const handleCameraClick = () => {
        if (cameraInputRef.current) {
            cameraInputRef.current.click();
        }
        // Don't close modal yet - wait for actual camera capture
    };

    // Close options without selecting
    const handleCloseOptions = () => {
        setShowOptions(false);
    };

    // Default button appearance if no custom className is provided
    const defaultButtonClass = `flex items-center justify-center px-4 py-3 rounded-lg transition-all
        ${disabled || !canAddMoreImages || isUploading
            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-[#9CAF88] hover:bg-[#8A9B79] text-white'
        }
        w-full max-w-md text-base`;
        
    // Use custom button class if provided, otherwise use the default
    const finalButtonClass = buttonClassName || defaultButtonClass;
    
    // Use custom icon if provided, otherwise use the default
    const iconElement = buttonIcon || <FontAwesomeIcon icon={faUpload} className="mr-2" />;

    return (
        <div className="relative w-full flex flex-col items-center">
            {/* Main upload button with customizable appearance */}
            <button
                className={finalButtonClass}
                onClick={handleUploadClick}
                disabled={disabled || !canAddMoreImages || isUploading}
                aria-label="Upload images"
            >
                {isUploading ? (
                    <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                        <span>Uploading... {uploadProgress}%</span>
                    </>
                ) : (
                    <>
                        {iconElement}
                        <span>{buttonText} {maxImages && `(${currentCount}/${maxImages})`}</span>
                    </>
                )}
            </button>

            {/* Upload options popup with sage green theme */}
            {showOptions && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCloseOptions}>
                    <div className="bg-white rounded-lg p-6 w-96 max-w-md shadow-lg" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-xl font-semibold text-[#7A8B73]" style={{ fontFamily: "Playfair Display" }}>
                                Choose Upload Method
                            </h3>
                            <button 
                                onClick={handleCloseOptions}
                                className="text-gray-500 hover:text-gray-700 p-1 rounded-full"
                                aria-label="Close"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        
                        <div className="flex justify-between gap-6">
                            {/* Camera option */}
                            <button
                                className="flex-1 flex flex-col items-center justify-center p-5 bg-[#F0F4ED] hover:bg-[#E5EBE0] rounded-lg transition-all text-[#7A8B73]"
                                onClick={handleCameraClick}
                                aria-label="Take photo"
                            >
                                <div className="w-16 h-16 rounded-full bg-[#9CAF88] text-white flex items-center justify-center mb-3">
                                    <FontAwesomeIcon icon={faCamera} className="text-2xl" />
                                </div>
                                <span className="text-lg font-medium">Take Photo</span>
                            </button>
                            
                            {/* File upload option */}
                            <button
                                className="flex-1 flex flex-col items-center justify-center p-5 bg-[#F0F4ED] hover:bg-[#E5EBE0] rounded-lg transition-all text-[#7A8B73]"
                                onClick={handleFileSelectClick}
                                aria-label="Choose files"
                            >
                                <div className="w-16 h-16 rounded-full bg-[#9CAF88] text-white flex items-center justify-center mb-3">
                                    <FontAwesomeIcon icon={faImages} className="text-2xl" />
                                </div>
                                <span className="text-lg font-medium">Choose Files</span>
                            </button>
                        </div>
                        
                        <button
                            className="w-full mt-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-all font-medium"
                            onClick={handleCloseOptions}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Hidden file input */}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                multiple={maxImages - currentCount > 1}
                className="hidden"
                disabled={disabled || isUploading || !sessionId}
            />

            {/* Hidden camera input */}
            <input
                type="file"
                ref={cameraInputRef}
                onChange={handleCameraCapture}
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={disabled || isUploading || !sessionId}
            />

            {/* Error message with improved styling */}
            {error && (
                <div className="mt-3 py-2 px-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded text-sm">
                    {error}
                </div>
            )}
        </div>
    );
};

export default EnhancedImageUpload;
